import axios from 'axios';
import isDev from 'util/isDev';

// Store user ID globally so it can be accessed from error logging
let currentUserId: string | undefined;

// Mirrors the server-side guard in server/routes/logging.js. The server is the actual control —
// it sees every client version and reads the real request header — but dropping here too saves the
// request entirely. Meta's crawler alone accounted for ~86 chunk-load reports a day.
const CRAWLER_USER_AGENT =
  /meta-externalagent|Googlebot|bingbot|Applebot|GPTBot|Bytespider|ClaudeBot|PerplexityBot|facebookexternalhit|Twitterbot|Slackbot|LinkedInBot|AhrefsBot|SemrushBot/i;

export function isCrawlerUserAgent(userAgent: string | undefined) {
  return !!userAgent && CRAWLER_USER_AGENT.test(userAgent);
}

function isCrawler() {
  return typeof navigator !== 'undefined' && isCrawlerUserAgent(navigator.userAgent);
}

/**
 * Normalize ChunkLoadError messages for better aggregation in error reporting
 * Replaces specific chunk IDs and URLs with placeholders while preserving details in context
 */
function normalizeChunkLoadError(
  errorMessage: string,
  additionalData?: Record<string, unknown>,
): { normalizedMessage: string; enhancedContext: Record<string, unknown> } {
  const enhancedContext = { ...additionalData };

  // Pattern 1: "Loading chunk 82 failed. (timeout: https://...)"
  // Pattern 2: "Loading chunk 82 failed. (error: https://...)"
  // Pattern 3: "ChunkLoadError: Loading chunk 82 failed"
  const chunkLoadPattern = /Loading chunk (\d+|[a-zA-Z0-9_-]+) failed\.?\s*(?:\((?:timeout|error):\s*(.+?)\))?/i;
  const match = errorMessage.match(chunkLoadPattern);

  if (match) {
    const chunkId = match[1];
    const chunkUrl = match[2];

    // Preserve original error details in context
    enhancedContext.errorType = 'ChunkLoadError';
    enhancedContext.originalMessage = errorMessage;
    enhancedContext.chunkId = chunkId;

    if (chunkUrl) {
      enhancedContext.chunkUrl = chunkUrl;
      // Normalize the message with placeholders
      const normalizedMessage = errorMessage
        .replace(chunkLoadPattern, 'Loading chunk [CHUNK_ID] failed. ($2: [CHUNK_URL])')
        .replace(chunkUrl, '[CHUNK_URL]');
      return { normalizedMessage, enhancedContext };
    } else {
      // No URL in the error message
      const normalizedMessage = errorMessage.replace(chunkLoadPattern, 'Loading chunk [CHUNK_ID] failed.');
      return { normalizedMessage, enhancedContext };
    }
  }

  // Check for generic ChunkLoadError without specific format
  if (
    errorMessage.toLowerCase().includes('chunkloaderror') ||
    (errorMessage.toLowerCase().includes('chunk') && errorMessage.toLowerCase().includes('failed'))
  ) {
    enhancedContext.errorType = 'ChunkLoadError';
    enhancedContext.originalMessage = errorMessage;
  }

  // Return unchanged if not a chunk load error
  return { normalizedMessage: errorMessage, enhancedContext };
}

/**
 * Set the current user ID for error logging
 * Call this after authentication
 */
export function setGcpUserId(userId: string | undefined): void {
  currentUserId = userId;
}

/**
 * Get the current user ID
 */
function getCurrentUserId(): string | undefined {
  return currentUserId;
}

/**
 * Send error data to GCP backend endpoint
 */
async function sendToGcpEndpoint(payload: {
  message: string;
  stack?: string;
  severity: string;
  additionalContext?: Record<string, unknown>;
}): Promise<void> {
  try {
    // Don't send errors in development to avoid noise
    if (isDev()) {
      console.log('[GCP Logging - Dev Mode]', payload);
      return;
    }

    if (isCrawler()) {
      return;
    }

    const errorPayload = {
      message: payload.message,
      stack: payload.stack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      user: getCurrentUserId(),
      severity: payload.severity,
      additionalContext: payload.additionalContext,
    };

    // Send to backend endpoint (non-blocking)
    await axios.post('/log-client-error', errorPayload, {
      timeout: 5000,
      // Don't throw on error - silent fail
      validateStatus: () => true,
    });
  } catch (error) {
    // Silent fail - don't throw errors from error logging
    if (isDev()) {
      console.error('Failed to send error to GCP:', error);
    }
  }
}

/**
 * Log an error to GCP Error Reporting
 *
 * @param error - Error object or error message
 * @param message - Optional additional message
 * @param additionalData - Optional context data
 */
export function logErrorToGcp(error: Error | any, message?: string, additionalData?: Record<string, unknown>): void {
  // Unpack nested exceptions and collect all messages
  const messages: string[] = [];

  // Add custom message first if provided
  if (message) {
    messages.push(message);
  }

  // Walk through error chain and collect all messages
  let currentError = error;
  while (currentError) {
    const msg = currentError?.message || currentError?.toString();
    if (msg && msg !== '[object Object]') {
      messages.push(msg);
    }
    // Check for nested error (common patterns: cause, error, inner)
    currentError = currentError?.cause || currentError?.error;
  }

  const errorMessage = messages.length > 0 ? messages.join(' : ') : 'Unknown error';

  // Extract stack if available
  const stack = error?.stack || undefined;

  // Check if this is a chunk loading error and add retry metadata
  const contextData = { ...additionalData };
  if (error?.isChunkLoadError) {
    contextData.chunkRetryAttempts = error.chunkRetryAttempts;
    contextData.chunkId = error.chunkId;
    contextData.networkOnline = navigator.onLine;
  }

  // Normalize ChunkLoadError messages for better aggregation
  const { normalizedMessage, enhancedContext } = normalizeChunkLoadError(errorMessage, contextData);

  // Log to console in development
  if (isDev()) {
    console.error('[GCP Error]', normalizedMessage, error, enhancedContext);
  }

  // Send to GCP (async, non-blocking)
  sendToGcpEndpoint({
    message: normalizedMessage,
    stack,
    severity: 'ERROR',
    additionalContext: enhancedContext,
  }).catch(() => {
    // Silent fail
  });
}

/**
 * Log a warning to GCP Error Reporting
 */
export function logWarningToGcp(message: string, additionalData?: Record<string, unknown>): void {
  if (isDev()) {
    console.warn('[GCP Warning]', message, additionalData);
  }

  sendToGcpEndpoint({
    message,
    severity: 'WARNING',
    additionalContext: additionalData,
  }).catch(() => {
    // Silent fail
  });
}

/**
 * Log an info message to GCP Error Reporting
 */
export function logInfoToGcp(message: string, additionalData?: Record<string, unknown>): void {
  if (isDev()) {
    console.info('[GCP Info]', message, additionalData);
  }

  sendToGcpEndpoint({
    message,
    severity: 'INFO',
    additionalContext: additionalData,
  }).catch(() => {
    // Silent fail
  });
}

/**
 * Set up global error handlers to capture uncaught errors
 * Call this once during app initialization
 */
export function setupGcpErrorHandlers(): void {
  // Capture uncaught errors
  window.addEventListener('error', event => {
    const error = event.error || event.message;
    logErrorToGcp(error, undefined, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  // Capture unhandled promise rejections
  window.addEventListener('unhandledrejection', event => {
    const reason = event.reason;
    const additionalData: Record<string, unknown> = {
      reasonType: typeof reason,
      pathname: window.location.pathname,
    };

    // Extract useful info from the rejection reason
    if (reason instanceof Error) {
      additionalData.message = reason.message;
      additionalData.stack = reason.stack;
      additionalData.name = reason.name;
    } else if (reason && typeof reason === 'object') {
      additionalData.error = (reason as any).error;
      additionalData.errorDescription = (reason as any).error_description;
      additionalData.message = (reason as any).message;
      additionalData.status = (reason as any).status;
    } else {
      additionalData.reason = String(reason);
    }

    logErrorToGcp(
      reason instanceof Error ? reason : new Error('Unhandled promise rejection'),
      'Unhandled promise rejection',
      additionalData,
    );
  });
}
